import React, { useEffect, useState, useCallback, useRef } from 'react';
import './SessionManager.css';

// ── Timing Constants ──────────────────────────────────────────────────────────
const IDLE_WARNING_MS  = 5  * 60 * 1000; // 5 min idle → show warning
const IDLE_LOGOUT_MS   = 60 * 1000;      // 60 s after warning → auto-logout
const ACTIVITY_EVENTS  = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

// ── Tab-Close Sentinel ────────────────────────────────────────────────────────
// We store a "tab alive" flag in sessionStorage.
// sessionStorage is cleared when the tab is closed (unlike localStorage).
// On every fresh page load, if the flag is NOT set, this is a brand-new tab
// (or the user closed and reopened) → wipe token and force re-login.
const TAB_ALIVE_KEY  = 'datalift_tab_alive';
const TAB_LOGGED_KEY = 'datalift_tab_logged_in';

function enforceTabCloseSecurity() {
  const tabAlive   = sessionStorage.getItem(TAB_ALIVE_KEY);
  const tabLogged  = sessionStorage.getItem(TAB_LOGGED_KEY);

  // If neither flag is present → brand-new tab or closed tab re-opened
  if (!tabAlive && !tabLogged) {
    // Wipe any persisted auth tokens so the user must re-login
    localStorage.removeItem('dms_auth_token');
    sessionStorage.removeItem('dms_auth_token');
  }

  // Mark this tab as alive for the current browser session
  sessionStorage.setItem(TAB_ALIVE_KEY, '1');
}

// Call immediately on module load (before React renders)
enforceTabCloseSecurity();

// ── Component ─────────────────────────────────────────────────────────────────
function SessionManager() {
  const [phase, setPhase]       = useState('active'); // 'active' | 'warning' | 'loggedout'
  const [countdown, setCountdown] = useState(60);     // seconds remaining in warning phase

  const idleTimerRef     = useRef(null);
  const countdownRef     = useRef(null);
  const countdownValRef  = useRef(60);

  // ── Logout helper ────────────────────────────────────────────────────────
  const doLogout = useCallback((reason = 'idle') => {
    console.log(`[SessionManager] Logging out. Reason: ${reason}`);
    localStorage.removeItem('dms_auth_token');
    sessionStorage.removeItem('dms_auth_token');
    sessionStorage.removeItem(TAB_ALIVE_KEY);
    sessionStorage.removeItem(TAB_LOGGED_KEY);
    // Redirect to login
    window.location.hash = '#/login';
    window.location.reload();
  }, []);

  // ── Start countdown (after idle warning) ────────────────────────────────
  const startCountdown = useCallback(() => {
    countdownValRef.current = 60;
    setCountdown(60);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      countdownValRef.current -= 1;
      setCountdown(countdownValRef.current);
      if (countdownValRef.current <= 0) {
        clearInterval(countdownRef.current);
        doLogout('idle-countdown-expired');
      }
    }, 1000);
  }, [doLogout]);

  // ── Show idle warning ────────────────────────────────────────────────────
  const triggerIdleWarning = useCallback(() => {
    setPhase('warning');
    startCountdown();
  }, [startCountdown]);

  // ── Reset idle timer on activity ─────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    // If warning is already showing, any activity dismisses it
    if (phase === 'warning') {
      clearInterval(countdownRef.current);
      setPhase('active');
      setCountdown(60);
    }

    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(triggerIdleWarning, IDLE_WARNING_MS);
  }, [phase, triggerIdleWarning]);

  // ── Attach activity listeners + start idle timer ─────────────────────────
  useEffect(() => {
    // Mark session as authenticated in sessionStorage
    const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
    if (token) {
      sessionStorage.setItem(TAB_LOGGED_KEY, '1');
      sessionStorage.setItem(TAB_ALIVE_KEY,  '1');
    }

    // Bind activity events
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));

    // Kick off the idle timer
    idleTimerRef.current = setTimeout(triggerIdleWarning, IDLE_WARNING_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stay Active button ───────────────────────────────────────────────────
  const handleStayActive = () => {
    clearInterval(countdownRef.current);
    setPhase('active');
    setCountdown(60);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(triggerIdleWarning, IDLE_WARNING_MS);
  };

  // Don't render anything when active
  if (phase !== 'warning') return null;

  // ── Warning Popup ─────────────────────────────────────────────────────────
  return (
    <div className="session-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="session-idle-title">
      <div className="session-modal-content">

        {/* Animated idle icon */}
        <div className="session-modal-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>

        <h2 id="session-idle-title" className="session-modal-title" style={{ color: '#f59e0b' }}>
          Are you still there?
        </h2>

        <p className="session-modal-desc">
          You've been idle for <strong>5 minutes</strong>.<br />
          Move your cursor or click <em>Stay Active</em> to continue.<br />
          Otherwise you'll be signed out in:
        </p>

        {/* Countdown circle */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: countdown > 30
              ? 'rgba(245, 158, 11, 0.12)'
              : 'rgba(239, 68, 68, 0.12)',
            border: `3px solid ${countdown > 30 ? '#f59e0b' : '#ef4444'}`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.4s ease'
          }}>
            <span style={{
              fontSize: 26, fontWeight: 800,
              color: countdown > 30 ? '#f59e0b' : '#ef4444',
              lineHeight: 1
            }}>{countdown}</span>
            <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>secs</span>
          </div>
        </div>

        <div className="session-modal-actions">
          <button className="btn-stay-logged-in" onClick={handleStayActive} autoFocus>
            ✓ Stay Active
          </button>
          <button className="btn-sign-out" onClick={() => doLogout('user-requested')}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionManager;
